import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiSortAscending, HiSortDescending } from 'react-icons/hi';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import { apiPaths } from 'src/services/api/apiPaths';
import {
  FilterProvider,
  useFilterContext,
} from 'src/components/app/context/FilterContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import ErrorHandler from 'src/components/common/ErrorHandler';
import FilterDisplay from 'src/components/common/FilterDisplay';
import PageFrame from 'src/components/common/PageFrame';
import { useUserList } from 'src/components/userAdmin/hooks/useUserList';
import UserAdminProvider, {
  useUserAdmin,
} from 'src/components/userAdmin/UserAdminContext';
import UserBulkEditDialog from 'src/components/userAdmin/UserBulkEditDialog';
import UserCheckbox from 'src/components/userAdmin/UserListPage/components/UserCheckbox';
import UserListAddModal from 'src/components/userAdmin/UserListPage/components/UserListAddModal';
import UserListFilterSelection from 'src/components/userAdmin/UserListPage/components/UserListFilterSelection';
import { UserListFooter } from 'src/components/userAdmin/UserListPage/components/UserListFooter';
import { formatLabel } from 'src/components/userAdmin/UserListPage/filterTree';

import { useUserListColumns } from '../hooks/useUserListColumns';

import { styles } from './index.styles';

export interface UserListPageProps {}

export const UserListPage: FC<UserListPageProps> = () => {
  const { handleSearchSubmit, handleSearchStringChange } = useUserList();
  const { validation, searchString } = useUserAdmin();
  const {
    pageSize,
    bulkEditingRequestState,
    handleBulkEditDialogOpen,
    handleRemoveUser,
    isFullSelection,
    setIsFullSelection,
    loading,
    page,
    searchResults,
    searchResultsCount,
  } = useUserAdmin();
  const {
    goToDetail,
    selectionCount,
    hideUserAdminDownload,
    downloading,
    handleDownloadClick,
    handleColumnHeaderClick,
    handlePageChange,
    handleSelectionModelChange,
    selectionModel,
  } = useUserList();

  const { t } = useTranslation(['admin', 'common']);
  const theme = useTheme();
  const { filterGroups } = useFilterContext();
  const { user } = useUserContext();

  const [isAddModalOpen, setAddModalOpen] = useState(false);

  const userListColumns = useUserListColumns(
    t,
    goToDetail,
    handleRemoveUser,
    user,
  );

  return (
    <PageFrame
      className="UserListPage-root"
      sidebar={<UserListFilterSelection />}
    >
      <Stack spacing={1} direction="column" sx={styles.dataGridContainer}>
        <Box>
          <Typography variant="h3" component="h6">
            {t('admin:List.UserAdministration')}
          </Typography>
        </Box>

        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
            {!!Object.keys(filterGroups).length && (
              <FilterDisplay withShowMoreButton formatLabel={formatLabel(t)} />
            )}
          </Box>

          <Box sx={{ flexGrow: 0, flexShrink: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              <LoadingButton
                data-test-id="user-admin-bulk-edit-button"
                disabled={!selectionCount}
                loading={bulkEditingRequestState?.status === 'pending'}
                onClick={handleBulkEditDialogOpen}
                variant="cta"
                color="secondary"
              >
                {t('admin:List.BulkEditOptions')}
              </LoadingButton>

              <Button
                variant="cta"
                color="secondary"
                data-test-id="user-admin-new-user-button"
                onClick={() => setAddModalOpen(true)}
              >
                {t('admin:List.AddUser')}
              </Button>

              {!hideUserAdminDownload && (
                <LoadingButton
                  loading={downloading?.status === 'pending'}
                  loadingPosition="start"
                  onClick={handleDownloadClick}
                  startIcon={<DownloadIcon />}
                  variant="cta"
                >
                  {t('common:download')}
                </LoadingButton>
              )}
            </Stack>
          </Box>
        </Stack>
        <Box
          component="form"
          onSubmit={handleSearchSubmit}
          sx={styles.searchFieldWrapper}
        >
          <TextField
            data-test-id="user-admin-search-list"
            error={!!validation}
            helperText={validation}
            id="search-input"
            placeholder={t('admin:List.SearchForNameOrEmail')}
            onChange={handleSearchStringChange}
            size="small"
            fullWidth
            value={searchString}
          />
        </Box>

        <Box sx={styles.dataGridWrapper}>
          <DataGrid
            checkboxSelection
            columns={userListColumns}
            slots={{
              footer: UserListFooter as any,
              baseCheckbox: UserCheckbox,
              columnSortedAscendingIcon: () => (
                <HiSortAscending size={16} color={theme.colors.primary[600]} />
              ),
              columnSortedDescendingIcon: () => (
                <HiSortDescending size={16} color={theme.colors.primary[600]} />
              ),
              columnUnsortedIcon: () => (
                <HiSortDescending size={16} color={theme.colors.neutral[700]} />
              ),
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'first_name', sort: 'desc' }],
              },
            }}
            slotProps={{
              footer: {
                isFullSelection,
                onSelectAll: () => setIsFullSelection(true),
                rowCount: searchResultsCount || 0,
                rowsPerPage: pageSize,
              } as any,
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableDensitySelector
            disableRowSelectionOnClick
            loading={loading}
            onColumnHeaderClick={handleColumnHeaderClick}
            onPaginationModelChange={handlePageChange}
            onRowSelectionModelChange={handleSelectionModelChange}
            paginationModel={{
              page: page - 1,
              pageSize: pageSize,
            }}
            paginationMode="server"
            rowCount={searchResultsCount}
            rows={searchResults}
            pageSizeOptions={[pageSize]}
            rowSelectionModel={selectionModel}
            sx={styles.dataGrid}
          />
        </Box>
      </Stack>
      <UserBulkEditDialog
        downloading={downloading}
        selectionCount={selectionCount}
        selectionModel={Array.from(selectionModel.ids).map(Number)}
      />
      <UserListAddModal
        isOpen={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
      />
      <ErrorHandler intercept ignoredEndpoints={[apiPaths.ORG_UNIT_CHILDREN]} />
    </PageFrame>
  );
};

// Wrap this page in a separate FilterProvider as its call of the useOrgTreeState-hook would run into racing conditions
// with the global initialization of the PlanActionContext and its call of the useOrgTreeState-hook.
const UserListPageWrapper: FC = () => (
  <FilterProvider allowCombinedFilters>
    <UserAdminProvider>
      <UserListPage />
    </UserAdminProvider>
  </FilterProvider>
);
export default UserListPageWrapper;
